import React from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';

import { theme } from '@garnish/constants';
import { Container, useResponsive } from '@garnish/core';
import { useNavbarContainerHeight } from '@garnish/navigation';

import { PaymentMethod } from '@order/features-ordering';

import { GiftCardCheckoutCardImage } from '../GiftCardCheckoutCardImage';
import { GiftCardCheckoutContainer } from '../GiftCardCheckoutContainer';
import { GiftCardCheckoutForm } from '../GiftCardCheckoutForm';
import { GiftCardCheckoutTitle } from '../GiftCardCheckoutTitle';

import type { GiftCardCheckoutFormFieldRules, GiftCardCheckoutFormReturn } from '../../form';

// ─────────────────────────────────────────────────────────────────────────────

export const GiftCardCheckoutContent = (props: GiftCardCheckoutContentProps) => {
  const {
    purchaseAttempt,
    screenTitle,
    sectionTitles,
    giftCardImage,
    form,
    rules,
    paymentMethods,
    submitForm,
    submitPayment,
    incrementPurchaseAttempt,
  } = props;

  const { minWidth } = useResponsive();
  const { paddingTopStyle: navbarPaddingTopStyle } = useNavbarContainerHeight();

  // ─── Large Breakpoints ───────────────────────────────────────────────

  if (minWidth.isMD) {
    return (
      <GiftCardCheckoutContainer>
        <View style={styles.columnsContainer}>
          {giftCardImage ? (
            <View style={styles.imageColumn}>
              <GiftCardCheckoutCardImage
                url={giftCardImage.url}
                staticUrl={giftCardImage.staticImageUrl}
                accessibilityLabel={giftCardImage.a11yLabel}
              />
            </View>
          ) : null}

          <ScrollView style={styles.formColumn} contentContainerStyle={navbarPaddingTopStyle}>
            <GiftCardCheckoutTitle>{screenTitle}</GiftCardCheckoutTitle>

            <GiftCardCheckoutForm
              purchaseAttempt={purchaseAttempt}
              incrementPurchaseAttempt={incrementPurchaseAttempt}
              sectionTitles={sectionTitles}
              form={form}
              rules={rules}
              paymentMethods={paymentMethods}
              submitForm={submitForm}
              submitPayment={submitPayment}
            />
          </ScrollView>
        </View>
      </GiftCardCheckoutContainer>
    );
  }

  // ─── Small Breakpoints ───────────────────────────────────────────────

  return (
    <GiftCardCheckoutContainer>
      <GiftCardCheckoutTitle>{screenTitle}</GiftCardCheckoutTitle>

      {giftCardImage ? (
        <Container>
          <GiftCardCheckoutCardImage
            url={giftCardImage.url}
            staticUrl={giftCardImage.staticImageUrl}
            accessibilityLabel={giftCardImage.a11yLabel}
          />
        </Container>
      ) : null}

      <GiftCardCheckoutForm
        purchaseAttempt={purchaseAttempt}
        incrementPurchaseAttempt={incrementPurchaseAttempt}
        sectionTitles={sectionTitles}
        form={form}
        rules={rules}
        paymentMethods={paymentMethods}
        submitForm={submitForm}
        submitPayment={submitPayment}
      />
    </GiftCardCheckoutContainer>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  columnsContainer: {
    flexDirection: 'row',
    flex: 1,
  },
  imageColumn: {
    width: '50%',
    backgroundColor: theme.colors.LINEN,
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing['10'],
  },
  formColumn: {
    width: '50%',
    backgroundColor: theme.colors.APP_BACKGROUND,
    paddingTop: theme.spacing['12'],
    paddingBottom: theme.spacing['10'],
    paddingHorizontal: theme.spacing['15'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type GiftCardCheckoutContentProps = {
  purchaseAttempt: number;
  incrementPurchaseAttempt: () => void;
  screenTitle?: string;
  sectionTitles?: {
    amount: string;
    details: string;
    deliveryDate: string;
    deliveryMethod: string;
    recipientEmail: string;
    paymentMethod: string;
  };
  giftCardImage?: {
    url: string;
    staticImageUrl?: string;
    a11yLabel: string;
  };
  form: GiftCardCheckoutFormReturn;
  rules: GiftCardCheckoutFormFieldRules;
  paymentMethods: readonly PaymentMethod[];
  submitForm: () => Promise<void>;
  submitPayment: (confirmPayment: () => Promise<void>) => Promise<void>;
};
